import { useState, type ReactNode } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { LiquidGlassRail } from "@/workshop/components/canvas/liquid-glass-rail";
import {
  LIQUID_CANVAS_ITEMS,
  LIQUID_NAV_ITEMS,
} from "@/workshop/components/canvas/liquid-glass-rail-data";

const MOCK_FRAMES = [
  { id: "f1", title: "Research brief", color: "bg-accent" },
  { id: "f2", title: "Landing copy", color: "bg-blue-400" },
  { id: "f3", title: "Bug triage", color: "bg-pink-400" },
];

const MOCK_TICKS = [
  { id: "brief", label: "Research brief", color: "bg-accent" },
  { id: "copy", label: "Landing copy", color: "bg-blue-400" },
  { id: "triage", label: "Bug triage", color: "bg-pink-400" },
  { id: "draft", label: "Open draft", color: "bg-purple-400" },
];

function PreviewFrame({ title, className }: { title: string; className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-glass-stroke-subtle bg-glass-fill/40 p-4 shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
    >
      <div className="mb-3 h-2 w-24 rounded-full bg-glass-stroke-subtle" />
      <div className="grid grid-cols-2 gap-2">
        <div className="h-16 rounded-lg bg-glass-fill-heavy" />
        <div className="h-16 rounded-lg bg-glass-fill-heavy" />
      </div>
      <p className="mt-3 truncate text-xs font-medium text-secondary">
        {title}
      </p>
    </div>
  );
}

function PrototypeSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-2xl border border-glass-stroke-light bg-glass-fill/35 p-5 shadow-glass backdrop-blur-glass-sm">
      <div className="mb-5">
        <h2 className="text-sm font-semibold text-primary">{title}</h2>
        <p className="mt-1 max-w-xl text-xs leading-5 text-secondary">
          {description}
        </p>
      </div>
      <div className="relative flex min-h-[520px] flex-1 overflow-hidden rounded-[28px] border border-glass-stroke-subtle bg-canvas">
        {children}
      </div>
    </section>
  );
}

function MockCanvasContent() {
  return (
    <div className="relative flex flex-1 items-center justify-center p-10">
      <div className="grid w-full max-w-[540px] grid-cols-2 gap-4">
        {MOCK_FRAMES.map((frame) => (
          <PreviewFrame key={frame.id} title={frame.title} />
        ))}
        <div className="rounded-xl border border-dashed border-glass-stroke-light bg-glass-fill/25 p-4">
          <div className="flex items-center gap-2 text-xs text-muted">
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-glass-stroke-subtle">
              <Icons.Plus className="h-3.5 w-3.5" />
            </span>
            Drop or create a frame
          </div>
        </div>
      </div>
    </div>
  );
}

export function LiquidGlassRailPage() {
  const [activeNav, setActiveNav] = useState("frames");
  const [activeCanvas, setActiveCanvas] = useState("pan");
  const [activeCompact, setActiveCompact] = useState("fit");
  const [activeTick, setActiveTick] = useState("brief");

  return (
    <div
      className="min-h-screen bg-canvas px-8 py-7 text-primary"
      data-testid="liquid-glass-rail-page"
    >
      <div className="mb-7 max-w-3xl">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-accent">
          Native app kit direction
        </p>
        <h1 className="mt-2 text-2xl font-semibold">
          Liquid Glass Vertical Rail
        </h1>
        <p className="mt-2 text-sm leading-6 text-secondary">
          Native AppKit rail structure with tab-style hover, active navigation
          affordance, and frame ticks above the canvas controls.
        </p>
      </div>

      <div className="grid gap-6">
        <PrototypeSection
          title="Canvas rail with Native AppKit contract"
          description="One adaptive glass capsule, larger rounded glyphs, tab-style hover movement, and ruler ticks for frame context."
        >
          <div className="relative z-10 flex w-20 items-center justify-center">
            <LiquidGlassRail
              items={LIQUID_CANVAS_ITEMS}
              activeItem={activeCanvas}
              onActiveItemChange={setActiveCanvas}
              label="Canvas control rail"
              testId="liquid-canvas-rail"
              ticks={MOCK_TICKS}
              activeTick={activeTick}
              onActiveTickChange={setActiveTick}
            />
          </div>
          <MockCanvasContent />
        </PrototypeSection>
      </div>

      <section className="mt-6 rounded-2xl border border-glass-stroke-light bg-glass-fill/35 p-5 shadow-glass backdrop-blur-glass-sm">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <h2 className="text-sm font-semibold text-primary">
              Navigation-only reference
            </h2>
            <p className="mt-1 max-w-xl text-xs leading-5 text-secondary">
              Tab-bar behavior without frame ticks. Useful if we later split
              top-level navigation from canvas commands.
            </p>
            <div className="mt-5">
              <LiquidGlassRail
                items={LIQUID_NAV_ITEMS}
                activeItem={activeNav}
                onActiveItemChange={setActiveNav}
                label="Navigation rail"
                testId="liquid-nav-rail"
              />
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-primary">
              Compact production fit
            </h2>
            <p className="mt-1 max-w-xl text-xs leading-5 text-secondary">
              Same behavior in a narrower footprint for the current canvas rail
              slot. One Native AppKit rail capsule, 32px command targets, and ruler
              ticks above the controls.
            </p>
            <div className="mt-5 flex items-center gap-8">
              <LiquidGlassRail
                items={LIQUID_CANVAS_ITEMS}
                activeItem={activeCompact}
                onActiveItemChange={setActiveCompact}
                label="Compact canvas rail"
                testId="liquid-compact-rail"
                ticks={MOCK_TICKS}
                activeTick={activeTick}
                onActiveTickChange={setActiveTick}
                compact
              />
              <div className="grid max-w-xl grid-cols-3 gap-3">
                {MOCK_FRAMES.map((frame) => (
                  <div
                    key={frame.id}
                    className="flex items-center gap-2 rounded-xl border border-glass-stroke-subtle bg-glass-fill/40 px-3 py-2 shadow-glass-sm backdrop-blur-glass-sm"
                  >
                    <span
                      className={cn("h-2 w-2 rounded-full", frame.color)}
                    />
                    <span className="truncate text-xs text-secondary">
                      {frame.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
